import { useState, useContext, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { ParagraphText, UBButton } from "../../../../../Libraries";
import {
  CalendarIconNew,
  DownloadIconBlack,
} from "../../../../../assets/icons/icon";
import { RangePicker } from "../../../../common";
import styles from "./ReportPageHeader.module.css";
import { LanguageContext } from "../../../../../context/LanguageContext";
import {
  getReportFilterDisplayLabels,
  getStartAndEndDate,
  formatRange,
} from "../../../../../utils";
import { useReportDateContext } from "../../../../../context/ReportDateContext";

const getDefaultSelectedDate = (defaultFilter) => {
  if (!defaultFilter) return null;
  const { startDate, endDate } = getStartAndEndDate(defaultFilter);
  if (!startDate || !endDate) return null;
  return {
    start_date: startDate.toDate ? startDate.toDate() : new Date(startDate),
    end_date: endDate.toDate ? endDate.toDate() : new Date(endDate),
  };
};

const ReportPageHeader = ({
  title,
  id = "",
  hideActions = false,
  dateFilters,
  onFilterDate,
  minDate,
  maxDate,
  defaultFilter = "",
  resetKey,
  onExport,
  exportLoading = false,
}) => {
  const sharedDateRef = useReportDateContext();
  const [I18n] = useContext(LanguageContext);
  const REPORT_FILTER_DISPLAY_LABELS = getReportFilterDisplayLabels(I18n);
  const mountedRef = useRef(false);

  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const shared = sharedDateRef?.current;
    if (shared?.startDate && shared?.endDate && !shared?.filter) {
      return {
        start_date: shared.startDate.toDate ? shared.startDate.toDate() : new Date(shared.startDate),
        end_date: shared.endDate.toDate ? shared.endDate.toDate() : new Date(shared.endDate),
      };
    }
    return getDefaultSelectedDate(defaultFilter);
  });
  const [selectedLabel, setSelectedLabel] = useState(() => {
    const shared = sharedDateRef?.current;
    if (shared?.startDate && shared?.endDate && !shared?.filter) {
      return formatRange({ start_date: shared.startDate, end_date: shared.endDate }, I18n);
    }
    return defaultFilter ? REPORT_FILTER_DISPLAY_LABELS[defaultFilter] || defaultFilter : "";
  });

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      const shared = sharedDateRef?.current;
      if (shared?.startDate && shared?.endDate && !shared?.filter) return;
    }
    setSelectedDate(getDefaultSelectedDate(defaultFilter));
    setSelectedLabel(defaultFilter ? REPORT_FILTER_DISPLAY_LABELS[defaultFilter] || defaultFilter : "");
  }, [defaultFilter, resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box id={`${id}_header`} className={styles.header}>
      <ParagraphText id={`${id}_title`} className={styles.title}>{title}</ParagraphText>
      {!hideActions && (
        <Box id={`${id}_actions`} className={styles.actions}>
          <Box id={`${id}_date_wrap`} className={`${styles.exportWrap} summaryDateBox`}>
            <UBButton
              id={`${id}_select_date`}
              title={selectedLabel || I18n.select_date}
              iconComponent={<CalendarIconNew width={12} height={12} />}
              className={`${styles.exportButton} ${styles.selectDate}`}
              onClick={() => setIsDateOpen((prev) => !prev)}
            />
            {isDateOpen && (
              <RangePicker
                showDirectPicker
                isOpen={isDateOpen}
                setIsOpen={setIsDateOpen}
                onFilterDate={(start, end, filter) => {
                  setIsDateOpen(false);
                  setSelectedDate(
                    start && end ? { start_date: start, end_date: end } : null,
                  );
                  if (filter) {
                    setSelectedLabel(
                      REPORT_FILTER_DISPLAY_LABELS[filter] || filter,
                    );
                  } else if (start && end) {
                    setSelectedLabel(
                      formatRange({ start_date: start, end_date: end }, I18n),
                    );
                  }
                  onFilterDate?.(start, end, filter);
                }}
                rangeValue={
                  selectedDate?.start_date && selectedDate?.end_date
                    ? [selectedDate.start_date, selectedDate.end_date]
                    : []
                }
                className={styles.rangePickerWrapper}
                noClearBtn
                dateFilters={dateFilters}
                minDate={minDate}
                maxDate={maxDate}
              />
            )}
          </Box>
          <Box id={`${id}_export_wrap`} className={`${styles.exportWrap} summaryDateBox`}>
            <UBButton
              id={`${id}_export`}
              title={I18n.export}
              iconComponent={<DownloadIconBlack width={12} height={12} />}
              className={styles.exportButton}
              loading={exportLoading}
              disabled={exportLoading}
              onClick={() => !exportLoading && setIsExportOpen((prev) => !prev)}
            />
            {isExportOpen && (
              <RangePicker
                showDirectPicker
                isOpen={isExportOpen}
                setIsOpen={setIsExportOpen}
                onFilterDate={(start, end) => {
                  setIsExportOpen(false);
                  if (start && end && onExport) onExport(start, end);
                }}
                rangeValue={[]}
                className={styles.rangePickerWrapper}
                noClearBtn
                dateFilters={dateFilters}
                minDate={minDate}
                maxDate={maxDate}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ReportPageHeader;
